import { useState } from "react";
import { Button } from "../ui/button";
import { motion } from "framer-motion";
import { DynamicDownloadForm } from "./DynamicDownloadForm";
import { Id } from "../../convex/_generated/dataModel";
import { isRTL, getTextDirection, getRTLClasses, getDirAttribute } from "../../utils/rtl";

interface HeroProps {
  title: string;
  subtitle: string;
  highlights: string[];
  backgroundImage?: string;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  showDownloadForm?: boolean;
  projectId: Id<"projects">;
  language?: string;
  downloadFormContent?: {
    title?: string;
    subtitle?: string;
    labels?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      phone?: string;
      interest?: string;
      previousPurchases?: string;
      investmentTimeline?: string;
      continue?: string;
      back?: string;
      submit?: string;
      submitting?: string;
      thankYou?: string;
      willContactYou?: string;
    };
    interestOptions?: { value: string; label: string }[];
    previousPurchasesOptions?: { value: string; label: string }[];
    investmentTimelineOptions?: { value: string; label: string }[];
    success?: {
      title?: string;
      description?: string;
    };
    error?: {
      title?: string;
      description?: string;
    };
    privacyText?: string;
  };
}

const Hero = ({ 
  title, 
  subtitle, 
  highlights, 
  backgroundImage = "https://springfield-re.com/storage/burj-vista/img17.jpg",
  primaryButtonText = "Download Brochure",
  secondaryButtonText = "Speak to Property Expert",
  showDownloadForm = true,
  projectId,
  language = "en",
  downloadFormContent
}: HeroProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // RTL detection
  const isCurrentLanguageRTL = isRTL(language);
  const textDirection = getTextDirection(language);
  const rtlClasses = getRTLClasses(language);
  const dirAttribute = getDirAttribute(language);

  const handleFormSuccess = () => {
    // Optional: trigger brochure download or other actions

  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleSpeakToExpert = () => {
    const leadFormSection = document.getElementById('leadForm');
    if (leadFormSection) {
      leadFormSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={`relative min-h-screen w-full ${rtlClasses}`} dir={dirAttribute}>
      {/* Background Image with Enhanced Overlay */}
      <div className="absolute inset-0">
        <img
          src={backgroundImage}
          alt="Luxury Dubai Property"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/70 to-black/90" />
      </div>

      {/* Content */}
      <div className="relative z-10 flex min-h-screen items-center">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative rounded-2xl bg-black/30 backdrop-blur-md p-6 md:p-12 max-w-2xl mx-auto text-center"
          >
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className={`mb-4 text-3xl md:text-5xl lg:text-6xl font-bold text-white tracking-tight ${isCurrentLanguageRTL ? 'text-right' : 'text-center'}`}
            >
              {title}
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className={`mb-8 text-lg md:text-xl lg:text-2xl text-gray-200 font-light ${isCurrentLanguageRTL ? 'text-right' : 'text-center'}`}
            >
              {subtitle}
            </motion.p>

            {/* Highlights with Animation */}
            {highlights.length > 0 && (
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="mb-10 space-y-4"
              >
                {highlights.map((highlight, index) => (
                  highlight && (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: isCurrentLanguageRTL ? 20 : -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                      className={`flex items-center justify-center text-white ${
                        isCurrentLanguageRTL ? 'space-x-reverse space-x-3' : 'space-x-3'
                      }`}
                    >
                      <svg
                        className="h-6 w-6 text-primary flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      <span className="text-lg md:text-xl font-medium">{highlight}</span>
                    </motion.div>
                  )
                ))}
              </motion.div>
            )}

            {/* CTA Buttons with Animation */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className={`flex flex-col space-y-4 sm:flex-row sm:space-y-0 justify-center ${
                isCurrentLanguageRTL ? 'sm:space-x-reverse sm:space-x-4' : 'sm:space-x-4'
              }`}
            >
              {showDownloadForm && (
                <Button
                  size="lg"
                  className="bg-primary text-lg font-semibold hover:bg-primary/90 transition-all duration-300 transform hover:scale-105 flex items-center justify-center gap-3 py-6 px-8"
                  onClick={handleOpenModal}
                  data-download-trigger="true"
                >
                  {primaryButtonText}
                  <svg
                    className="w-6 h-6 animate-bounce"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                </Button>
              )}
              <Button
                variant="outline"
                size="lg"
                onClick={handleSpeakToExpert}
                className="border-2 text-lg font-semibold text-white hover:bg-white hover:text-black transition-all duration-300 transform hover:scale-105 py-6 px-8"
              >
                {secondaryButtonText}
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Download Form Modal */}
      {showDownloadForm && (
        <DynamicDownloadForm 
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          projectId={projectId}
          language={language}
          content={downloadFormContent}
          onSuccess={handleFormSuccess}
        />
      )}
    </div>
  );
};

export default Hero; 